import React from 'react';
import PropTypes from 'prop-types';
import { image_error_replace as imageErrorReplace } from '../../../../util_functions';
import { filtersShape, funnelShape, slotShape, studentShape, teacherEvalShape } from './shapes';
import {
  filterLabel,
  fitSummary,
  placedBySummary,
  teacherProfileUrl,
  warningText
} from './constants';

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

function KV({ items }) {
  return (
    <dl className="rv2-kv">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

KV.propTypes = {
  items: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)).isRequired
};

function ListBadges({ items, variant }) {
  if (!items || items.length === 0) return <span className="rv2-hint">none</span>;
  return items.map((i) => (
    <span key={i} className={`rv2-badge rv2-badge-${variant}`} title={i}>
      {filterLabel(i)}
    </span>
  ));
}

ListBadges.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.string.isRequired
};

ListBadges.defaultProps = { items: [] };

export function TimezoneFallbackBadge() {
  return (
    <span
      className="rv2-badge rv2-badge-warn"
      title="Student has no valid timezone on record; the server falls back to UTC"
    >
      timezone unknown, using UTC
    </span>
  );
}

export function StudentSummary({ student, slot, phaseAOnly }) {
  const goals = student.goals || [];
  const genderPref = (
    <>
      {show(student.teacher_gender_preference)}{' '}
      {student.gender_pref_inferred ? (
        <span
          className="rv2-badge rv2-badge-warn"
          title="Inferred: female student in a Middle-East country with no stated preference"
        >
          inferred
        </span>
      ) : null}
    </>
  );
  const goalBadges =
    goals.length === 0 ? (
      <span className="rv2-hint">
        none selected (goal score is 0 for every teacher, so availability alone decides fit)
      </span>
    ) : (
      goals.map((g) => (
        <span
          key={g.student_goal_id || g.display_name}
          className={`rv2-badge ${g.is_health ? 'rv2-badge-warn' : 'rv2-badge-neutral'}`}
          title={g.is_health ? `Health goal; requires teacher flag: ${g.required_flag}` : ''}
        >
          {g.display_name}
          {g.is_health ? ` (${g.required_flag || 'health'})` : ''}
        </span>
      ))
    );
  const timezone = (
    <>
      {show(student.timezone)} {student.timezone_is_fallback ? <TimezoneFallbackBadge /> : null}
    </>
  );
  let slotText = 'Phase A only (no slot)';
  if (!phaseAOnly && slot && slot.start_epoch) {
    slotText = `${slot.student_local || ''} (epoch ${slot.start_epoch})`;
  }
  return (
    <div className="rv2-card">
      <h3>Student (resolved inputs)</h3>
      <KV
        items={[
          ['Name', show(student.name)],
          ['Email', show(student.email)],
          ['ID / UUID', `${show(student.id)} / ${show(student.uuid)}`],
          ['Timezone', timezone],
          ['Gender', show(student.gender)],
          ['Country ISO', show(student.country_iso)],
          ['Ethnicity', show(student.ethnicity)],
          ['Teacher gender pref', genderPref],
          [
            'Prenatal',
            student.is_prenatal
              ? `${show(student.is_prenatal)} (requires prenatal-qualified teachers)`
              : show(student.is_prenatal)
          ],
          ['Referral', show(student.is_referral)],
          ['Trial student', show(student.is_trial)],
          ['Member', show(student.is_member)],
          ['Test user', show(student.is_test_user)],
          ['Slot (student local)', slotText]
        ]}
      />
      <div style={{ marginTop: 8 }}>
        <span className="rv2-hint">Goals: </span>
        {goalBadges}
      </div>
    </div>
  );
}

StudentSummary.propTypes = {
  student: studentShape.isRequired,
  slot: slotShape,
  phaseAOnly: PropTypes.bool.isRequired
};

StudentSummary.defaultProps = { slot: null };

function FunnelSummary({ funnel }) {
  return (
    <div className="rv2-funnel">
      <p>{funnel.text}</p>
      {funnel.steps.length > 0 ? (
        <ol className="rv2-funnel-steps">
          <li className="rv2-funnel-start">
            <strong>{funnel.steps[0].before}</strong> approved teachers
          </li>
          {funnel.steps.map((st) => (
            <li
              key={st.filter}
              className={st.relaxed ? 'rv2-funnel-relaxed' : ''}
              title={`${st.filter}: ${st.failing} teachers fail this filter overall (including ones already removed above)`}
            >
              <span className="rv2-funnel-label">{st.label}</span>
              {st.relaxed ? (
                <span className="rv2-hint"> relaxed, removed none</span>
              ) : (
                <>
                  <span className="rv2-funnel-removed">−{st.removed}</span>
                  <span className="rv2-funnel-remaining">→ {st.remaining} left</span>
                </>
              )}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

FunnelSummary.propTypes = { funnel: funnelShape.isRequired };

export function FiltersCard({ filters, meta, funnel }) {
  const relaxed = filters.relaxed || [];
  return (
    <div className="rv2-card">
      <h3>Filters &amp; relaxation</h3>
      {funnel ? <FunnelSummary funnel={funnel} /> : null}
      <KV
        items={[
          ['Phase A', <ListBadges key="a" items={filters.phase_a} variant="neutral" />],
          ['Phase B hard', <ListBadges key="bh" items={filters.phase_b_hard} variant="neutral" />],
          [
            'Phase B soft (active)',
            <ListBadges key="bs" items={filters.phase_b_soft_active} variant="info" />
          ],
          [
            'Relaxed (in order)',
            relaxed.length === 0 ? (
              <span className="rv2-hint">none</span>
            ) : (
              relaxed.map((r, i) => (
                <span key={r} className="rv2-badge rv2-badge-warn" title={r}>
                  {i + 1}. {filterLabel(r)}
                </span>
              ))
            )
          ],
          ['Pool after Phase A', show(filters.pool_after_phase_a)],
          ['Pool after Phase B', show(filters.pool_after_phase_b)],
          ['Request ID', show(meta.request_id)],
          ['Algo version', show(meta.algo_version)],
          ['Random seed', show(meta.random_seed)],
          ['Generated at', show(meta.generated_at)]
        ]}
      />
    </div>
  );
}

FiltersCard.propTypes = {
  filters: filtersShape.isRequired,
  meta: PropTypes.shape({
    request_id: PropTypes.string,
    algo_version: PropTypes.string,
    random_seed: PropTypes.number,
    generated_at: PropTypes.string
  }).isRequired,
  funnel: funnelShape
};

FiltersCard.defaultProps = { funnel: null };

export function TopCards({ shown }) {
  return (
    <div className="rv2-card">
      <h3>Top {shown.length} shown</h3>
      {shown.length === 0 ? (
        <div className="rv2-empty">No teachers to show for these inputs.</div>
      ) : (
        <div className="rv2-top-cards">
          {shown.map((t) => (
            <div key={t.teacher_id} className="rv2-top-card">
              <div className="rv2-top-card-head">
                <img
                  src={t.profile_photo || ''}
                  alt={t.name || 'teacher'}
                  onError={imageErrorReplace}
                />
                <div>
                  <div>
                    <strong>
                      #{t.rank}{' '}
                      {teacherProfileUrl(t) ? (
                        <a
                          className="rv2-teacher-link"
                          href={teacherProfileUrl(t)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t.name}
                        </a>
                      ) : (
                        t.name
                      )}
                    </strong>
                  </div>
                  <span
                    className="rv2-sub"
                    title={`fit_score ${t.fit_score} · decided_by ${t.decided_by}`}
                  >
                    id {t.teacher_id}
                  </span>
                </div>
              </div>
              <div className="rv2-top-card-summary">
                <div>{fitSummary(t)}</div>
                <div title={`decided_by: ${t.decided_by || ''}`}>{placedBySummary(t)}</div>
              </div>
              <div className="rv2-top-card-reason">
                {t.reason || <span className="rv2-hint">No reason text</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

TopCards.propTypes = {
  shown: PropTypes.arrayOf(teacherEvalShape).isRequired
};

export function TimingsStrip({ timings }) {
  const entries = Object.entries(timings || {});
  if (entries.length === 0) return null;
  return (
    <div className="rv2-timings" title="Server-side step timings in milliseconds">
      <span>Timings (ms):</span>
      {entries.map(([k, v]) => (
        <span
          key={k}
          className={`rv2-badge ${k === 'total' ? 'rv2-badge-info' : 'rv2-badge-neutral'}`}
        >
          {k}: {v}
        </span>
      ))}
    </div>
  );
}

TimingsStrip.propTypes = {
  timings: PropTypes.objectOf(PropTypes.number)
};

TimingsStrip.defaultProps = { timings: null };

export function WarningsBanner({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="rv2-warn-banner" role="status">
      <strong>Heads up</strong>
      <ul>
        {warnings.map((w) => {
          const raw = typeof w === 'string' ? w : JSON.stringify(w);
          return (
            <li key={raw} title={raw}>
              {warningText(w)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

WarningsBanner.propTypes = {
  warnings: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
};

WarningsBanner.defaultProps = { warnings: [] };
